import React, { useEffect } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Divider,
  Tooltip,
  Stack,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import { StarOutline } from '@mui/icons-material';
import { useFavorites } from '../hooks/useFavorites';
import {
  ThemedDeleteIcon,
  ThemedImportIcon,
} from '../../../../Common/Components/Icons';
import { NewFavoriteQuery } from '../types/query';

interface FavoritesDrawerProps {
  open: boolean;
  onClose: () => void;
  onSelectQuery: (favoriteQuery: NewFavoriteQuery) => void;
  setError: (value: React.SetStateAction<string>) => void;
}

const FavoritesDrawer: React.FC<FavoritesDrawerProps> = ({
  open,
  onClose,
  onSelectQuery,
  setError,
}) => {
  const { favorites, reloadFavorites, loader, deleteFavoriteQuery } =
    useFavorites({ setError });

  useEffect(() => {
    if (open) {
      reloadFavorites();
    }
  }, [open, reloadFavorites]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      {loader ? (
        <LinearProgress />
      ) : (
        <Box
          sx={{
            marginTop: 8,
            width: 400,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarOutline color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Favorite Queries
            </Typography>
          </Box>
          <Divider />

          <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
            {favorites.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No favorites saved.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {favorites.map((fav) => (
                  <Card
                    key={fav.Id}
                    variant="outlined"
                    sx={{ borderRadius: 3 }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {fav.QueryName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(fav.CreatedAt).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Tooltip title="Delete Query">
                            <IconButton
                              onClick={() => {
                                deleteFavoriteQuery(fav.Id);
                                onSelectQuery({
                                  Id: '00000000-0000-0000-0000-000000000000',
                                  QueryName: '',
                                  QueryText: '',
                                });
                              }}
                            >
                              <ThemedDeleteIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Load Query">
                            <IconButton
                              onClick={() =>
                                onSelectQuery(fav as NewFavoriteQuery)
                              }
                            >
                              <ThemedImportIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default FavoritesDrawer;
